import type React from 'react';
import { Suspense, createContext, lazy, useContext, useEffect, useState } from 'react';
import type { FormConfig } from '../config/types';

interface FormConfigContextType {
  config: FormConfig;
  isLoading: boolean;
  error: Error | null;
  portalComponent: React.ReactNode;
}

export const FormConfigContext = createContext<FormConfigContextType>({
  config: {} as FormConfig,
  portalComponent: null,
  isLoading: true,
  error: null,
});

interface FormConfigProviderProps {
  children: React.ReactNode;
  configName: string;
}

export const loadFormConfig = async (configName?: string): Promise<FormConfig> => {
  try {
    if (!configName) {
      return {} as FormConfig;
    }
    const module = await import(`../config/configs/${configName}`);
    return module.default as FormConfig;
  } catch (error) {
    console.error('Error loading form configuration:', error);
    return {} as FormConfig;
  }
};

export const FormConfigProvider: React.FC<FormConfigProviderProps> = ({ children, configName }) => {
  const [config, setConfig] = useState<FormConfig>({} as FormConfig);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const PortalComponent = lazy(() => import(`../screens/Portals/${configName}`));

  useEffect(() => {
    const loadConfig = async () => {
      try {
        setIsLoading(true);
        const loadedConfig = await loadFormConfig(configName);
        setConfig(loadedConfig);
        setError(null);
      } catch (err) {
        setError(err instanceof Error ? err : new Error('Failed to load form configuration'));
      } finally {
        setIsLoading(false);
      }
    };

    loadConfig();
  }, [configName]);

  return (
    <FormConfigContext.Provider
      value={{
        config,
        isLoading,
        error,
        portalComponent: (
          <Suspense fallback="Loading...">
            <PortalComponent />
          </Suspense>
        ),
      }}
    >
      {children}
    </FormConfigContext.Provider>
  );
};
